import { Component, computed, inject, input, signal } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";

import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { RIGHT_PANEL_CONFIG } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { TransactionLinkService } from "@bitwarden/web-vault/app/services/DataService/transaction/transaction-link.service";
import { TransactionConversionService } from "@bitwarden/web-vault/app/services/DataService/transaction/transaction-conversion.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { I18nService } from "@vault/common/abstractions/i18n.service";
import { LogService } from "@vault/common/abstractions/log.service";
import { TransactionDirection } from "@vault/web-vault/app/models/enum/transactionDirection";
import { TransactionLinkType } from "@vault/web-vault/app/models/types/transaction.types";
import { TransactionView } from "@vault/web-vault/app/models/view/transaction/transaction.view";

/** A candidate partner plus its precomputed display fields. */
interface LinkCandidate {
  transaction: TransactionView;
  linkType: TransactionLinkType;
  description: string;
  date: Date;
  amount: number;
  symbol: string;
  direction: TransactionDirection;
  account: string;
}

/**
 * Right-panel picker for the manual transfer/conversion link flow. Lists the
 * plausible partners for a source transaction (opposite direction, forms a valid
 * transfer/conversion) and links the chosen pair via {@link TransactionLinkService}.
 * Beta-gated at the entry point in {@link TransactionDetailsComponent}.
 */
@Component({
  templateUrl: "./link-transaction-picker.component.html",
})
export class LinkTransactionPickerComponent {
  /** The transaction the user is linking from. */
  source = input<TransactionView | null>(null);

  protected TransactionDirection = TransactionDirection;
  protected search = signal("");
  /** A link write is in flight; the list stays rendered but is not clickable. */
  protected linking = signal(false);

  private linkService = inject(TransactionLinkService);
  private conversionService = inject(TransactionConversionService);
  private userStoreService = inject(UserStoreService);
  private i18nService = inject(I18nService);
  private platformUtilsService = inject(PlatformUtilsService);
  private logService = inject(LogService);

  constructor(public dialogRef: MatDialogRef<LinkTransactionPickerComponent>) {}

  /** id -> account name, for showing which account a candidate belongs to. */
  private accountNames = computed(() => {
    const accounts = this.userStoreService.enrichAccounts.collection() ?? [];
    return new Map(accounts.map((account) => [account.id, account.name]));
  });

  /** Valid partners for the source, reactive to the store and the search box. */
  protected candidates = computed<LinkCandidate[]>(() => {
    const source = this.source();
    if (!source) {
      return [];
    }
    const names = this.accountNames();
    const term = this.search().trim().toLowerCase();

    return this.linkService
      .getCandidates(source)
      .map((transaction) => ({
        transaction,
        // getCandidates already filtered to pairs with a non-null link type, so this
        // re-derivation can't be null.
        linkType: this.conversionService.getLinkType(source, transaction)!,
        description: transaction.description,
        date: transaction.postDate,
        amount: transaction.quantity.actualQuantity.amount,
        symbol: transaction.quantity.actualQuantity.symbol,
        direction: transaction.direction,
        account: names.get(transaction.accountLink.id) ?? "",
      }))
      .filter((candidate) => !term || candidate.description?.toLowerCase().includes(term))
      .sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0));
  });

  updateSearch(value: string): void {
    this.search.set(value);
  }

  /**
   * Link the source to the chosen candidate and close on success. A failed vault
   * write is caught and surfaced so the panel doesn't sit there looking like
   * nothing happened; `linking` stops a second click from overwriting the pair
   * mid-save. `unexpectedError` (the write blew up) is deliberately distinct from
   * `linkedTransferError` (not a valid pair).
   */
  async link(candidate: LinkCandidate): Promise<void> {
    const source = this.source();
    if (!source || this.linking()) {
      return;
    }
    this.linking.set(true);
    try {
      const linkType = await this.linkService.linkPair(source, candidate.transaction);
      if (linkType) {
        this.platformUtilsService.showToast(
          "success",
          "",
          this.i18nService.t("linkedTransactionSuccess"),
        );
        this.dialogRef?.close();
        return;
      }
      this.platformUtilsService.showToast("error", "", this.i18nService.t("linkedTransferError"));
    } catch (e) {
      this.logService.error(e instanceof Error ? (e.stack ?? e.message) : JSON.stringify(e));
      this.platformUtilsService.showToast("error", "", this.i18nService.t("unexpectedError"));
    } finally {
      this.linking.set(false);
    }
  }

  static open(source: TransactionView, dialog: MatDialog, onBack: () => void): void {
    dialog.open(DialogWrapperComponent, {
      ...RIGHT_PANEL_CONFIG,
      disableClose: false,
      data: {
        title: "linkTransaction",
        showBackButton: true,
        component: LinkTransactionPickerComponent,
        componentInputs: { source },
        onBackClicked: onBack,
      },
    });
  }
}
